import React from 'react';
import {
	IonButton,
	IonContent,
	IonItem,
	IonHeader,
	IonPage,
	IonIcon,
	IonToolbar,
	IonGrid,
	IonRow,
	IonCol,
	IonLabel,
	IonInput,
	IonTitle,
	IonLoading,
	useIonViewDidEnter
} from '@ionic/react';
import {copy, ticket} from 'ionicons/icons';

import {ASANA_COPY_DOD_TASK, ASANA_LOOKUP_TASK} from "../../schemas/developertools";

import {useMutation} from "@apollo/client";

const DodCopy: React.FC<any> = () => {
	const FULL_DOD_TASK_ID: string = "1204555933746073";
	const PARTIAL_DOD_TASK_ID: string = "1208063913662633";
	const DEFAULT_DOD_TASK_ID: string = FULL_DOD_TASK_ID;

	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const [dodTaskName, setDodTaskName] = React.useState<string | null>();
	const [dodTaskId, setDodTaskId] = React.useState<string | null | undefined>("1204555933746073");
	const [copyToTaskName, setCopyToTaskName] = React.useState<string | null>();
	const [copyToTaskId, setCopyToTaskId] = React.useState<string | null | undefined>();
	const [validating, setValidating] = React.useState(false);
	const [validated, setValidate] = React.useState(false);

	const [lookupTaskMutation] = useMutation(ASANA_LOOKUP_TASK);
	const [copyDodTaskMutation] = useMutation(ASANA_COPY_DOD_TASK);

	const loadData = async (taskId: string) => {
		try {

			let lookupResult = await lookupTaskMutation({variables: {taskId: taskId}});
			if (lookupResult.data.lookupTask.success) {
				return lookupResult.data.lookupTask.taskName;
			}
			return null;
		} catch (e) {
			console.log("error:");
			console.log(e);
			alert("Error: " + e);
			return null;
		}
	};


	const addDodToTask = async (f: any) => {
		setIsSubmitting(true);
		f.preventDefault();
		if (!validated) {
			alert("Please validate the entered IDs first.");
			setIsSubmitting(false);
			return false;
		}
		if (dodTaskId?.length != 16) {
			alert("DOD task id is not valid!");
			setIsSubmitting(false);
			return;
		}
		if (copyToTaskId?.length != 16) {
			alert("Copy to task id is not valid!");
			setIsSubmitting(false);
			return;
		}

		let taskType: 'Full' | 'Partial' | 'Custom' = 'Custom';
		if (dodTaskId === FULL_DOD_TASK_ID) {
			taskType = 'Full';
		} else if (dodTaskId === PARTIAL_DOD_TASK_ID) {
			taskType = 'Partial';
		}
		const nameForCopiedTask = `Definition of Done (${taskType}) for ${copyToTaskName}`;

		// start copying
		// send to graphql
		let copyResult = await copyDodTaskMutation({
			variables: {
				dodTaskId: dodTaskId,
				copyToTaskId: copyToTaskId,
				copyToTaskName: copyToTaskName,
				taskName: dodTaskName,
				nameForCopiedTask: nameForCopiedTask,
			}
		});
		if (copyResult.data.copyDodTask.success) {
			alert("DOD copied to target task.");
			setCopyToTaskId(null);
			setCopyToTaskName(null);
			setIsSubmitting(false);
			return false;
		}
		alert("Error: " + copyResult.data.copyDodTask.error);
		setIsSubmitting(false);
		return false;

	}

	const validateTasks = async (f: any) => {
		f.preventDefault();
		setDodTaskName(null);
		setCopyToTaskName(null);

		if (dodTaskId?.length != 16) {
			alert("DOD Task ID should be 16 digits long.");
			return false;
		}
		if (copyToTaskId?.length != 16) {
			alert("Copy To Task ID should be 16 digits long.");
			return false;
		}
		setValidating(true);
		let dodName = await loadData(dodTaskId);
		setDodTaskName(dodName);
		let copyName = await loadData(copyToTaskId);
		setCopyToTaskName(copyName);

		if (!dodName) {
			alert("Could not find DOD Task ID entered.");
			setValidate(false);
			return false;
		}
		if (!copyName) {
			alert("Could not find Copy To Task ID entered.");
			setValidate(false);
			return false;
		}
		if (dodName && copyName) {
			setValidate(true);
		}
		return false;
	};


	// repopulates the DOD task id for the user incase it was changed
	function populateDodTaskId(e: any) {
		e.preventDefault();
		setDodTaskId(DEFAULT_DOD_TASK_ID);
		return false;
	}

	function dodTaskIdChange(e: any) {
		setValidating(false);
		setValidate(false);
		setDodTaskId(e.detail.value);
	}

	function copyToTaskChange(e: any) {
		setValidating(false);
		setValidate(false);
		setCopyToTaskId(e.detail.value);
	}

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonTitle>Dev Tools &mdash; Asana DOD Copy</IonTitle>
				</IonToolbar>
			</IonHeader>
			<IonContent fullscreen className="dev-tools">
				<p>Tools for use by the IT department only.</p>

				<IonGrid>
					<IonRow>
						<IonCol>
							<b>Current Asana DOD Template:</b>

						</IonCol>
						<IonCol>
							<IonInput fill="outline" label="Asana DOD Template Task ID:" labelPlacement="floating" type="text" placeholder="Enter text" value={dodTaskId}
									  onIonChange={(e) => dodTaskIdChange(e)}>

							</IonInput>
							<IonButton onClick={(e) => populateDodTaskId(e)}>
								<IonIcon icon={copy}/>
							</IonButton>
							<IonButton onClick={() => setDodTaskId(FULL_DOD_TASK_ID)} color={(dodTaskId === FULL_DOD_TASK_ID ? 'secondary' : '')}>
								Full
							</IonButton>
							<IonButton onClick={(f) => setDodTaskId(PARTIAL_DOD_TASK_ID)} color={(dodTaskId === PARTIAL_DOD_TASK_ID ? 'secondary' : '')}>
								Partial
							</IonButton>
						</IonCol>
					</IonRow>
					<IonRow>
						<IonCol>
							<b>Asana Task to add DOD to:</b>
						</IonCol>
						<IonCol>
							<IonInput fill="outline" label="Asana Task ID" labelPlacement="floating" type="text" placeholder="Enter text" value={copyToTaskId}
									  onIonChange={(e) => copyToTaskChange(e)}></IonInput>
						</IonCol>
					</IonRow>
					<IonRow>
						<IonCol>

						</IonCol>
						<IonCol>
							<IonButton onClick={(f) => validateTasks(f)}>Validate Tasks</IonButton>
						</IonCol>
					</IonRow>
					{validating &&
						<IonGrid>
							<IonRow>
								<IonCol>
									<h3>DOD Task</h3>
									<p><b>{dodTaskName}</b><br/>
										{dodTaskId}</p>
								</IonCol>
								<IonCol>
									<h3>Target Task</h3>
									<p><b>{copyToTaskName}</b><br/>
										{copyToTaskId}</p>
								</IonCol>
							</IonRow>

						</IonGrid>
					}

					{validated ?
						<IonRow>
							<IonCol>

							</IonCol>

							<IonCol>
								<IonButton disabled={isSubmitting} onClick={(f) => addDodToTask(f)}>Copy DOD to Target Task</IonButton>
							</IonCol>
						</IonRow>
						:
						<IonRow>
							<IonCol></IonCol>
							<IonCol></IonCol>
						</IonRow>
					}
				</IonGrid>
			</IonContent>
		</IonPage>
	)
};

export default DodCopy;